import { useState, type ReactNode } from "react";
import {
  CARRIERS,
  type AcceptedAtPrice,
  type CancellationAnswerInput,
  type Carrier,
  type DespatchInput,
  type ProposeDateInput,
  type PurchaseOrderLine,
  type ServiceRef,
} from "@shared-core/data/supplier-portal-adapter";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  ACTION_WORDING,
  FORM_WORDING,
  carrierLabel,
  despatchDateLabel,
  despatchWording,
  platformToday,
  poundsToPence,
  priceIncreaseConfirmation,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Textarea } from "../../app/components/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { FormField } from "../../app/components/patterns/FormField";
import { FilePicker } from "../../app/components/patterns/FilePicker";

// The forms a line opens in place (one at a time). Each submits through the
// page, which re-reads the order when it goes through; a refusal's field
// errors sit under their inputs, with a summary above.

function FormFrame({
  title,
  banner,
  children,
  actions,
  onSubmit,
}: {
  title: string;
  banner: string | null;
  children: ReactNode;
  actions: ReactNode;
  onSubmit: () => void;
}) {
  return (
    <form
      noValidate
      aria-label={title}
      className="grid gap-3 rounded-lg border bg-card p-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p className="text-sm font-semibold">{title}</p>
      {banner && (
        <p role="alert" className="text-sm text-red-600">
          {banner}
        </p>
      )}
      {children}
      <div className="flex flex-wrap justify-end gap-2">{actions}</div>
    </form>
  );
}

function CancelButton({ onCancel }: { onCancel: () => void }) {
  return (
    <Button type="button" variant="ghost" onClick={onCancel}>
      {ACTION_WORDING.cancel}
    </Button>
  );
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "Can't meet the date": the earliest date the supplier can deliver, and an optional reason. */
export function ProposeDateForm({
  line,
  onSubmit,
  onCancel,
}: {
  line: PurchaseOrderLine;
  onSubmit: (input: ProposeDateInput) => Promise<void>;
  onCancel: () => void;
}) {
  const earliestAllowed = addDays(
    line.deliver_to.deliver_by ?? platformToday(),
    1,
  );
  const [earliestOn, setEarliestOn] = useState(earliestAllowed);
  const [reason, setReason] = useState("");
  const write = useFieldErrorWrite("We couldn't send your date to Pollards.");
  const id = `line-${line.line_no}`;
  return (
    <FormFrame
      title={FORM_WORDING.cantMeetTitle}
      banner={write.banner}
      onSubmit={() =>
        void write.run(() => onSubmit({ earliest_on: earliestOn, reason }))
      }
      actions={
        <>
          <CancelButton onCancel={onCancel} />
          <Button type="submit" disabled={write.isBusy}>
            {ACTION_WORDING.sendToPollards}
          </Button>
        </>
      }
    >
      <FormField
        id={`${id}-earliest`}
        label={FORM_WORDING.earliestDate}
        error={write.fieldErrors.earliest_on}
      >
        {(control) => (
          <Input
            {...control}
            type="date"
            min={earliestAllowed}
            value={earliestOn}
            onChange={(event) => setEarliestOn(event.target.value)}
          />
        )}
      </FormField>
      <FormField
        id={`${id}-date-reason`}
        label={FORM_WORDING.reasonOptional}
        error={write.fieldErrors.reason}
      >
        {(control) => (
          <Input
            {...control}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </FormField>
    </FormFrame>
  );
}

/** Reject: a reason is required; the line goes back to purchasing to choose another supplier. */
/**
 * *Accept at a different price* (spec 30 §L2e): the supplier gives its delivered price for the whole line. When the price
 * raises the cost we sent it reads, before it confirms, the same sentence for every increase (the trigger is never
 * revealed). What came of it is shown by the line once the write goes through.
 */
export function AcceptAtPriceForm({
  line,
  purchaseOrderNo,
  nextIncrease,
  onSubmit,
  onCancel,
}: {
  line: PurchaseOrderLine;
  purchaseOrderNo: string;
  nextIncrease: string | null;
  onSubmit: (costPence: number) => Promise<AcceptedAtPrice>;
  onCancel: () => void;
}) {
  const sent = line.cost_pence ?? 0;
  const [price, setPrice] = useState((sent / 100).toFixed(2));
  const write = useFieldErrorWrite("We couldn't send your price to Pollards.");
  const pence = poundsToPence(price);
  const confirmation =
    pence === null
      ? null
      : priceIncreaseConfirmation({
          purchaseOrderNo,
          fromPence: sent,
          toPence: pence,
          nextIncrease,
        });
  return (
    <FormFrame
      title={FORM_WORDING.acceptAtPriceTitle}
      banner={write.banner}
      onSubmit={() => {
        if (pence === null) return;
        void write.run(async () => {
          await onSubmit(pence);
        });
      }}
      actions={
        <>
          <CancelButton onCancel={onCancel} />
          <Button type="submit" disabled={write.isBusy || pence === null}>
            {ACTION_WORDING.acceptAtPrice}
          </Button>
        </>
      }
    >
      <FormField
        id={"line-" + line.line_no + "-price"}
        label={FORM_WORDING.acceptAtPriceLabel}
        hint={FORM_WORDING.acceptAtPriceHint}
        error={write.fieldErrors["lines.0.cost_pence"]}
      >
        {(control) => (
          <Input
            {...control}
            inputMode="decimal"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
          />
        )}
      </FormField>
      {confirmation && (
        <p role="note" className="text-sm text-muted-foreground">
          {confirmation}
        </p>
      )}
    </FormFrame>
  );
}

export function RejectForm({
  line,
  onSubmit,
  onCancel,
}: {
  line: PurchaseOrderLine;
  onSubmit: (reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const write = useFieldErrorWrite(
    "We couldn't send your rejection to Pollards.",
  );
  return (
    <FormFrame
      title={FORM_WORDING.rejectTitle}
      banner={write.banner}
      onSubmit={() => void write.run(() => onSubmit(reason))}
      actions={
        <>
          <CancelButton onCancel={onCancel} />
          <Button type="submit" variant="destructive" disabled={write.isBusy}>
            {ACTION_WORDING.rejectLine}
          </Button>
        </>
      }
    >
      <FormField
        id={`line-${line.line_no}-reject-reason`}
        label={FORM_WORDING.rejectReason}
        hint={FORM_WORDING.rejectHint}
        error={write.fieldErrors.reason}
      >
        {(control) => (
          <Textarea
            {...control}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </FormField>
    </FormFrame>
  );
}

/**
 * Answer Pollards' request to cancel the line (spec 30 §L2b): confirm it with the costs already incurred (ex VAT, £0.00
 * unless there are some), or refuse it with why. One form for both; the two buttons on the line open it in a mode.
 */
export function CancellationForm({
  line,
  mode,
  onSubmit,
  onCancel,
}: {
  line: PurchaseOrderLine;
  mode: "confirmed" | "refused";
  onSubmit: (input: CancellationAnswerInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [costs, setCosts] = useState("0.00");
  const [reason, setReason] = useState("");
  const write = useFieldErrorWrite("We couldn't send your answer to Pollards.");
  const confirming = mode === "confirmed";
  const pence = poundsToPence(costs);
  return (
    <FormFrame
      title={
        confirming
          ? FORM_WORDING.confirmCancellationTitle
          : FORM_WORDING.cannotCancelTitle
      }
      banner={write.banner}
      onSubmit={() => {
        if (confirming && pence === null) return;
        void write.run(() =>
          onSubmit(
            confirming
              ? { outcome: "confirmed", costs_incurred_pence: pence ?? 0 }
              : { outcome: "refused", reason },
          ),
        );
      }}
      actions={
        <>
          <CancelButton onCancel={onCancel} />
          <Button
            type="submit"
            variant={confirming ? "default" : "destructive"}
            disabled={write.isBusy}
          >
            {confirming
              ? ACTION_WORDING.confirmCancellation
              : ACTION_WORDING.cannotCancel}
          </Button>
        </>
      }
    >
      {confirming ? (
        <FormField
          id={`line-${line.line_no}-cancel-costs`}
          label={FORM_WORDING.cancellationCosts}
          hint={FORM_WORDING.cancellationCostsHint}
          error={
            write.fieldErrors.costs_incurred_pence ??
            (pence === null ? "Enter an amount like 12.50" : undefined)
          }
        >
          {(control) => (
            <Input
              {...control}
              inputMode="decimal"
              value={costs}
              onChange={(event) => setCosts(event.target.value)}
            />
          )}
        </FormField>
      ) : (
        <FormField
          id={`line-${line.line_no}-cancel-reason`}
          label={FORM_WORDING.cannotCancelReason}
          error={write.fieldErrors.reason}
        >
          {(control) => (
            <Textarea
              {...control}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </FormField>
      )}
    </FormFrame>
  );
}

/**
 * Say why the change Pollards made after the supplier had the order can't be made (spec 30 §L2c): the reason goes to
 * Pollards' buying team. Accepting the change needs no form.
 */
export function AmendmentRefusalForm({
  line,
  onSubmit,
  onCancel,
}: {
  line: PurchaseOrderLine;
  onSubmit: (reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const write = useFieldErrorWrite("We couldn't send your answer to Pollards.");
  return (
    <FormFrame
      title={FORM_WORDING.cannotMakeChangeTitle}
      banner={write.banner}
      onSubmit={() => void write.run(() => onSubmit(reason))}
      actions={
        <>
          <CancelButton onCancel={onCancel} />
          <Button type="submit" variant="destructive" disabled={write.isBusy}>
            {ACTION_WORDING.cannotMakeChange}
          </Button>
        </>
      }
    >
      <FormField
        id={`line-${line.line_no}-change-reason`}
        label={FORM_WORDING.cannotMakeChangeReason}
        error={write.fieldErrors.reason}
      >
        {(control) => (
          <Textarea
            {...control}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </FormField>
    </FormFrame>
  );
}

/** Upload a proof for Pollards' check (brief decision 8). */
export function ProofUploadForm({
  line,
  onSubmit,
  onCancel,
}: {
  line: PurchaseOrderLine;
  onSubmit: (file: File) => Promise<void>;
  onCancel: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const write = useFieldErrorWrite("We couldn't upload your proof.");
  return (
    <FormFrame
      title={FORM_WORDING.proofTitle}
      banner={write.banner}
      onSubmit={() => {
        if (file) void write.run(() => onSubmit(file));
      }}
      actions={
        <>
          <CancelButton onCancel={onCancel} />
          <Button type="submit" disabled={write.isBusy || !file}>
            {ACTION_WORDING.uploadProof}
          </Button>
        </>
      }
    >
      <FormField
        id={`line-${line.line_no}-proof`}
        label="Proof file"
        hint={FORM_WORDING.proofHint}
        error={write.fieldErrors.file}
      >
        {(control) => (
          <FilePicker
            {...control}
            file={file}
            onChange={setFile}
            accept="application/pdf,image/png,image/jpeg"
            prompt="Drop the proof here, or"
            chooseLabel="Choose file"
          />
        )}
      </FormField>
    </FormFrame>
  );
}

/**
 * Mark despatched, per delivery (screen 3): the quantity, the carrier and its tracking number, and the date. Own van
 * asks for a delivery date instead of tracking; "other" asks the carrier's name. *Part delivery…* sends fewer than
 * the rest of the line, which stays open for the next delivery. A service line is worded by its kind (posted,
 * packed, installed), has no carrier, and a mailing or installation uploads its proof
 * of posting or sign-off.
 */
export function DespatchForm({
  line,
  service,
  today,
  onSubmit,
  onCancel,
}: {
  line: PurchaseOrderLine;
  service: ServiceRef | null;
  today: string;
  onSubmit: (input: DespatchInput) => Promise<void>;
  onCancel: () => void;
}) {
  const remaining = line.quantity_to_send;
  const wording = despatchWording(service);
  const isService = line.service !== null;
  const [isPart, setIsPart] = useState(false);
  const [quantity, setQuantity] = useState(String(remaining));
  const [carrier, setCarrier] = useState<Carrier | "">(isService ? "" : "dpd");
  const [carrierName, setCarrierName] = useState("");
  const [tracking, setTracking] = useState("");
  const [despatchedOn, setDespatchedOn] = useState(today);
  const [deliverOn, setDeliverOn] = useState("");
  const [evidence, setEvidence] = useState<File | null>(null);
  const write = useFieldErrorWrite("We couldn't send this to Pollards.");
  const errors = write.fieldErrors;
  const id = `line-${line.line_no}-despatch`;
  const quantityNumber = Number(quantity);
  const left =
    remaining - (Number.isFinite(quantityNumber) ? quantityNumber : 0);

  const submit = () =>
    void write.run(() =>
      onSubmit({
        is_part: isPart,
        quantity: isPart ? Number.parseInt(quantity, 10) : remaining,
        carrier: isService ? null : carrier || null,
        carrier_name: carrierName,
        tracking_number: tracking,
        despatched_on: despatchedOn,
        deliver_on: deliverOn,
        evidence,
      }),
    );

  return (
    <FormFrame
      title={isPart ? `${wording.action}: part delivery` : wording.action}
      banner={write.banner}
      onSubmit={submit}
      actions={
        <>
          <CancelButton onCancel={onCancel} />
          {remaining > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsPart(!isPart);
                setQuantity(isPart ? String(remaining) : "");
              }}
            >
              {isPart ? ACTION_WORDING.wholeLine : ACTION_WORDING.partDelivery}
            </Button>
          )}
          <Button type="submit" disabled={write.isBusy}>
            {wording.action}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormField
          id={`${id}-quantity`}
          label={FORM_WORDING.quantity}
          error={errors.quantity}
          hint={
            isPart && left > 0 && quantity
              ? FORM_WORDING.stillToSend(left)
              : null
          }
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              inputMode="numeric"
              min={1}
              max={remaining}
              readOnly={!isPart}
              value={isPart ? quantity : String(remaining)}
              onChange={(event) => setQuantity(event.target.value)}
            />
          )}
        </FormField>
        {!isService && (
          <FormField
            id={`${id}-carrier`}
            label={FORM_WORDING.carrier}
            error={errors.carrier}
          >
            {(control) => (
              <Select
                value={carrier}
                onValueChange={(value) => setCarrier(value as Carrier)}
              >
                <SelectTrigger {...control} className="w-full">
                  <SelectValue placeholder={FORM_WORDING.chooseCarrier} />
                </SelectTrigger>
                <SelectContent>
                  {CARRIERS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {carrierLabel(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        )}
        {!isService && carrier === "other" && (
          <FormField
            id={`${id}-carrier-name`}
            label={FORM_WORDING.carrierName}
            error={errors.carrier_name}
          >
            {(control) => (
              <Input
                {...control}
                value={carrierName}
                onChange={(event) => setCarrierName(event.target.value)}
              />
            )}
          </FormField>
        )}
        {!isService && carrier !== "own_van" && carrier !== "" && (
          <FormField
            id={`${id}-tracking`}
            label={
              carrier === "other"
                ? `${FORM_WORDING.trackingNumber} (optional)`
                : FORM_WORDING.trackingNumber
            }
            error={errors.tracking_number}
          >
            {(control) => (
              <Input
                {...control}
                value={tracking}
                onChange={(event) => setTracking(event.target.value)}
              />
            )}
          </FormField>
        )}
        {!isService && carrier === "own_van" && (
          <FormField
            id={`${id}-deliver-on`}
            label={FORM_WORDING.deliveryDate}
            error={errors.deliver_on}
          >
            {(control) => (
              <Input
                {...control}
                type="date"
                min={despatchedOn}
                value={deliverOn}
                onChange={(event) => setDeliverOn(event.target.value)}
              />
            )}
          </FormField>
        )}
        <FormField
          id={`${id}-date`}
          label={despatchDateLabel(service)}
          error={errors.despatched_on}
        >
          {(control) => (
            <Input
              {...control}
              type="date"
              max={today}
              value={despatchedOn}
              onChange={(event) => setDespatchedOn(event.target.value)}
            />
          )}
        </FormField>
      </div>
      {wording.evidence && (
        <FormField
          id={`${id}-evidence`}
          label={wording.evidence}
          error={errors.evidence}
        >
          {(control) => (
            <FilePicker
              {...control}
              file={evidence}
              onChange={setEvidence}
              accept="application/pdf,image/png,image/jpeg"
              prompt={`Drop the ${wording.evidence?.toLowerCase()} here, or`}
              chooseLabel="Choose file"
            />
          )}
        </FormField>
      )}
      {!isService && (
        <p className="text-xs text-muted-foreground">
          {FORM_WORDING.carriersHint}
        </p>
      )}
    </FormFrame>
  );
}
